/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 * Destek hedefi ve görünür form verisi; Core tek kaynaktır.
 */
export type SupportTarget = 'QUBEPRO_SAAS' | 'TENANT_INTERNAL';

export function supportTargetForPurpose(purpose: 'BUSINESS' | 'QUBEPRO_HELP'): SupportTarget {
  return purpose === 'QUBEPRO_HELP' ? 'QUBEPRO_SAAS' : 'TENANT_INTERNAL';
}

export function readSupportTarget(value: unknown): SupportTarget | null {
  return value === 'QUBEPRO_SAAS' || value === 'TENANT_INTERNAL' ? value : null;
}

const TARGET_COPY = {
  tr: ['Destek hedefini seçin. Ekler ve kayıt yalnız seçtiğiniz destek ekibine gönderilir.', 'QubePro desteği', 'Şirketimin destek ekibi', 'Hedef seçilmedi', 'Gönderilecek metni ve ekleri kontrol edin. Önceki sohbet ve ekran verileri otomatik eklenmez.'],
  en: ['Choose a support destination. Attachments and the ticket go only to the selected support team.', 'QubePro support', 'My company support team', 'No destination selected', 'Review the text and attachments to be sent. Previous conversation and screen data are not added automatically.'],
  ar: ['اختر جهة الدعم. تُرسل المرفقات والتذكرة إلى فريق الدعم المحدد فقط.', 'دعم QubePro', 'فريق دعم شركتي', 'لم يتم اختيار جهة', 'راجع النص والمرفقات التي ستُرسل. لا تُضاف المحادثة السابقة وبيانات الشاشة تلقائيًا.'],
  de: ['Wählen Sie ein Supportziel. Anhänge und Ticket gehen nur an das gewählte Supportteam.', 'QubePro Support', 'Supportteam meiner Firma', 'Kein Ziel gewählt', 'Prüfen Sie Text und Anhänge vor dem Senden. Frühere Gespräche und Bildschirmdaten werden nicht automatisch hinzugefügt.'],
  fr: ['Choisissez une destination. Les pièces jointes et le ticket sont envoyés uniquement à cette équipe.', 'Support QubePro', 'Support de mon entreprise', 'Aucune destination choisie', 'Vérifiez le texte et les pièces jointes à envoyer. Les conversations précédentes et les données à l’écran ne sont pas ajoutées automatiquement.'],
  es: ['Seleccione un destino. Los archivos y el ticket se envían solo al equipo seleccionado.', 'Soporte de QubePro', 'Soporte de mi empresa', 'Sin destino seleccionado', 'Revise el texto y los archivos que se enviarán. La conversación anterior y los datos de pantalla no se añaden automáticamente.'],
  it: ['Scegli una destinazione. Allegati e ticket vengono inviati solo al team selezionato.', 'Supporto QubePro', 'Supporto della mia azienda', 'Nessuna destinazione scelta', 'Controlla il testo e gli allegati da inviare. Conversazioni precedenti e dati dello schermo non vengono aggiunti automaticamente.'],
  ru: ['Выберите службу поддержки. Вложения и обращение отправляются только выбранной команде.', 'Поддержка QubePro', 'Поддержка моей компании', 'Получатель не выбран', 'Проверьте отправляемый текст и вложения. Предыдущая переписка и данные экрана автоматически не добавляются.'],
  zh: ['请选择支持团队。附件和工单只会发送给所选团队。', 'QubePro 支持', '我公司的支持团队', '未选择接收方', '请检查将发送的文本和附件。之前的对话和屏幕数据不会自动添加。'],
  ja: ['サポート先を選択してください。添付ファイルとチケットは選択したチームだけに送信されます。', 'QubePro サポート', '自社サポートチーム', '送信先未選択', '送信するテキストと添付ファイルを確認してください。過去の会話や画面データは自動追加されません。'],
} as const;

export function supportTargetCopy(language = 'tr') {
  const key = language.toLowerCase().split('-')[0] as keyof typeof TARGET_COPY;
  const [choose, vendor, internal, unselected, review] = TARGET_COPY[key] ?? TARGET_COPY.en;
  return { choose, vendor, internal, unselected, review };
}
export function supportTargetLabel(target: unknown, language = 'tr') {
  const copy = supportTargetCopy(language);
  return target === 'QUBEPRO_SAAS' ? copy.vendor : target === 'TENANT_INTERNAL' ? copy.internal : copy.unselected;
}

/** Only fields visible in the support form. Context, history and model metadata never enter a ticket. */
export function buildSupportFormDraft(form: {
  supportTarget: SupportTarget | null;
  title: string; description: string; category: string; priority: string; steps: string;
}, applicationCode: string) {
  const supportTarget = readSupportTarget(form.supportTarget);
  if (!supportTarget) throw new Error('SUPPORT_TARGET_REQUIRED');
  return {
    supportTarget,
    title: form.title.trim() || undefined,
    description: form.description.trim() || undefined,
    category: form.category.trim() === 'DEVELOPMENT' ? 'REQUEST' : form.category.trim() || undefined,
    priority: form.priority.trim() || undefined,
    product: applicationCode,
    reproductionSteps: form.steps.trim() ? [form.steps.trim()] : [],
  };
}

const FORM_COPY = {
  "tr": {
    "form": "Destek kaydı",
    "target": "Destek hedefi",
    "title": "Başlık",
    "category": "Kategori",
    "priority": "Öncelik",
    "description": "Açıklama",
    "steps": "Tekrarlama adımları",
    "close": "Formu gizle",
    "submit": "Destek kaydı oluştur",
    "sending": "Gönderiliyor...",
    "titleHint": "Kısa sorun başlığı",
    "descriptionHint": "Sorunu, beklenen davranışı ve etkiyi yazın",
    "stepsHint": "Varsa adım adım nasıl oluştuğunu yazın",
    "capture": "Ekran görüntüsü ekle",
    "attach": "Görsel veya dosya ekle",
    "needText": "Önce başlık veya açıklama girin",
    "vendorHint": "QubePro ürünü, lisans, abonelik veya platform sorunu",
    "internalHint": "Şirketinizin kendi kullanıcıları ve iç işleyişi",
    "categories": {
      "REQUEST": "Talep",
      "ISSUE": "Hata",
      "SUGGESTION": "Öneri",
      "QUESTION": "Soru",
      "OTHER": "Diğer"
    },
    "priorities": {
      "low": "Düşük",
      "medium": "Orta",
      "high": "Yüksek",
      "urgent": "Acil"
    }
  },
  "en": {
    "form": "Support ticket",
    "target": "Support destination",
    "title": "Title",
    "category": "Category",
    "priority": "Priority",
    "description": "Description",
    "steps": "Steps to reproduce",
    "close": "Hide form",
    "submit": "Create support ticket",
    "sending": "Sending...",
    "titleHint": "Brief issue title",
    "descriptionHint": "Describe the issue, expected behavior and impact",
    "stepsHint": "If applicable, describe how to reproduce the issue",
    "capture": "Attach screenshot",
    "attach": "Attach image or file",
    "needText": "Enter a title or description first",
    "vendorHint": "QubePro product, license, subscription or platform issue",
    "internalHint": "Your company’s users and internal operations",
    "categories": {
      "REQUEST": "Request",
      "ISSUE": "Issue",
      "SUGGESTION": "Suggestion",
      "QUESTION": "Question",
      "OTHER": "Other"
    },
    "priorities": {
      "low": "Low",
      "medium": "Medium",
      "high": "High",
      "urgent": "Urgent"
    }
  },
  "ar": {
    "form": "تذكرة دعم",
    "target": "جهة الدعم",
    "title": "العنوان",
    "category": "الفئة",
    "priority": "الأولوية",
    "description": "الوصف",
    "steps": "خطوات إعادة المشكلة",
    "close": "إخفاء النموذج",
    "submit": "إنشاء تذكرة دعم",
    "sending": "جارٍ الإرسال...",
    "titleHint": "عنوان مختصر للمشكلة",
    "descriptionHint": "صف المشكلة والسلوك المتوقع والتأثير",
    "stepsHint": "اكتب خطوات إعادة المشكلة إن وجدت",
    "capture": "إرفاق لقطة شاشة",
    "attach": "إرفاق صورة أو ملف",
    "needText": "أدخل العنوان أو الوصف أولاً",
    "vendorHint": "مشكلة في منتج QubePro أو الترخيص أو الاشتراك أو المنصة",
    "internalHint": "مستخدمو شركتك وعملياتها الداخلية",
    "categories": {
      "REQUEST": "طلب",
      "ISSUE": "مشكلة",
      "SUGGESTION": "اقتراح",
      "QUESTION": "سؤال",
      "OTHER": "أخرى"
    },
    "priorities": {
      "low": "منخفضة",
      "medium": "متوسطة",
      "high": "عالية",
      "urgent": "عاجلة"
    }
  },
  "de": {
    "form": "Supportticket",
    "target": "Supportziel",
    "title": "Titel",
    "category": "Kategorie",
    "priority": "Priorität",
    "description": "Beschreibung",
    "steps": "Schritte zur Reproduktion",
    "close": "Formular ausblenden",
    "submit": "Supportticket erstellen",
    "sending": "Wird gesendet...",
    "titleHint": "Kurzer Problemtitel",
    "descriptionHint": "Beschreiben Sie Problem, erwartetes Verhalten und Auswirkungen",
    "stepsHint": "Beschreiben Sie gegebenenfalls die Schritte zur Reproduktion",
    "capture": "Screenshot anhängen",
    "attach": "Bild oder Datei anhängen",
    "needText": "Geben Sie zuerst Titel oder Beschreibung ein",
    "vendorHint": "Problem mit QubePro Produkt, Lizenz, Abonnement oder Plattform",
    "internalHint": "Benutzer und interne Abläufe Ihres Unternehmens",
    "categories": {
      "REQUEST": "Anfrage",
      "ISSUE": "Fehler",
      "SUGGESTION": "Vorschlag",
      "QUESTION": "Frage",
      "OTHER": "Sonstiges"
    },
    "priorities": {
      "low": "Niedrig",
      "medium": "Mittel",
      "high": "Hoch",
      "urgent": "Dringend"
    }
  },
  "fr": {
    "form": "Ticket de support",
    "target": "Destination du support",
    "title": "Titre",
    "category": "Catégorie",
    "priority": "Priorité",
    "description": "Description",
    "steps": "Étapes de reproduction",
    "close": "Masquer le formulaire",
    "submit": "Créer un ticket",
    "sending": "Envoi en cours...",
    "titleHint": "Titre bref du problème",
    "descriptionHint": "Décrivez le problème, le comportement attendu et son impact",
    "stepsHint": "Indiquez les étapes pour reproduire le problème, le cas échéant",
    "capture": "Joindre une capture d’écran",
    "attach": "Joindre une image ou un fichier",
    "needText": "Saisissez d’abord un titre ou une description",
    "vendorHint": "Problème de produit, licence, abonnement ou plateforme QubePro",
    "internalHint": "Utilisateurs et opérations internes de votre entreprise",
    "categories": {
      "REQUEST": "Demande",
      "ISSUE": "Problème",
      "SUGGESTION": "Suggestion",
      "QUESTION": "Question",
      "OTHER": "Autre"
    },
    "priorities": {
      "low": "Faible",
      "medium": "Moyenne",
      "high": "Élevée",
      "urgent": "Urgente"
    }
  },
  "es": {
    "form": "Ticket de soporte",
    "target": "Destino de soporte",
    "title": "Título",
    "category": "Categoría",
    "priority": "Prioridad",
    "description": "Descripción",
    "steps": "Pasos para reproducir",
    "close": "Ocultar formulario",
    "submit": "Crear ticket de soporte",
    "sending": "Enviando...",
    "titleHint": "Título breve del problema",
    "descriptionHint": "Describa el problema, el comportamiento esperado y su impacto",
    "stepsHint": "Indique los pasos para reproducir el problema, si corresponde",
    "capture": "Adjuntar captura",
    "attach": "Adjuntar imagen o archivo",
    "needText": "Introduzca primero un título o una descripción",
    "vendorHint": "Problema de producto, licencia, suscripción o plataforma QubePro",
    "internalHint": "Usuarios y operaciones internas de su empresa",
    "categories": {
      "REQUEST": "Solicitud",
      "ISSUE": "Problema",
      "SUGGESTION": "Sugerencia",
      "QUESTION": "Pregunta",
      "OTHER": "Otro"
    },
    "priorities": {
      "low": "Baja",
      "medium": "Media",
      "high": "Alta",
      "urgent": "Urgente"
    }
  },
  "it": {
    "form": "Ticket di supporto",
    "target": "Destinazione supporto",
    "title": "Titolo",
    "category": "Categoria",
    "priority": "Priorità",
    "description": "Descrizione",
    "steps": "Passaggi per riprodurre",
    "close": "Nascondi modulo",
    "submit": "Crea ticket di supporto",
    "sending": "Invio in corso...",
    "titleHint": "Breve titolo del problema",
    "descriptionHint": "Descrivi il problema, il comportamento previsto e l’impatto",
    "stepsHint": "Se applicabile, indica come riprodurre il problema",
    "capture": "Allega schermata",
    "attach": "Allega immagine o file",
    "needText": "Inserisci prima un titolo o una descrizione",
    "vendorHint": "Problema relativo a prodotto, licenza, abbonamento o piattaforma QubePro",
    "internalHint": "Utenti e operazioni interne della tua azienda",
    "categories": {
      "REQUEST": "Richiesta",
      "ISSUE": "Problema",
      "SUGGESTION": "Suggerimento",
      "QUESTION": "Domanda",
      "OTHER": "Altro"
    },
    "priorities": {
      "low": "Bassa",
      "medium": "Media",
      "high": "Alta",
      "urgent": "Urgente"
    }
  },
  "ru": {
    "form": "Обращение в поддержку",
    "target": "Служба поддержки",
    "title": "Заголовок",
    "category": "Категория",
    "priority": "Приоритет",
    "description": "Описание",
    "steps": "Шаги воспроизведения",
    "close": "Скрыть форму",
    "submit": "Создать обращение",
    "sending": "Отправка...",
    "titleHint": "Краткий заголовок проблемы",
    "descriptionHint": "Опишите проблему, ожидаемое поведение и последствия",
    "stepsHint": "При необходимости укажите шаги воспроизведения",
    "capture": "Прикрепить снимок экрана",
    "attach": "Прикрепить изображение или файл",
    "needText": "Сначала введите заголовок или описание",
    "vendorHint": "Проблема с продуктом, лицензией, подпиской или платформой QubePro",
    "internalHint": "Пользователи и внутренние процессы вашей компании",
    "categories": {
      "REQUEST": "Запрос",
      "ISSUE": "Проблема",
      "SUGGESTION": "Предложение",
      "QUESTION": "Вопрос",
      "OTHER": "Другое"
    },
    "priorities": {
      "low": "Низкий",
      "medium": "Средний",
      "high": "Высокий",
      "urgent": "Срочный"
    }
  },
  "zh": {
    "form": "支持工单",
    "target": "支持接收方",
    "title": "标题",
    "category": "类别",
    "priority": "优先级",
    "description": "描述",
    "steps": "重现步骤",
    "close": "隐藏表单",
    "submit": "创建支持工单",
    "sending": "正在发送...",
    "titleHint": "简短的问题标题",
    "descriptionHint": "请描述问题、预期行为和影响",
    "stepsHint": "如适用，请说明重现问题的步骤",
    "capture": "附加屏幕截图",
    "attach": "附加图片或文件",
    "needText": "请先输入标题或描述",
    "vendorHint": "QubePro 产品、许可证、订阅或平台问题",
    "internalHint": "贵公司的用户和内部运营",
    "categories": {
      "REQUEST": "请求",
      "ISSUE": "问题",
      "SUGGESTION": "建议",
      "QUESTION": "疑问",
      "OTHER": "其他"
    },
    "priorities": {
      "low": "低",
      "medium": "中",
      "high": "高",
      "urgent": "紧急"
    }
  },
  "ja": {
    "form": "サポートチケット",
    "target": "サポート先",
    "title": "件名",
    "category": "カテゴリ",
    "priority": "優先度",
    "description": "説明",
    "steps": "再現手順",
    "close": "フォームを閉じる",
    "submit": "チケットを作成",
    "sending": "送信中...",
    "titleHint": "問題の短い件名",
    "descriptionHint": "問題、期待する動作、影響を記入してください",
    "stepsHint": "該当する場合は再現手順を記入してください",
    "capture": "スクリーンショットを添付",
    "attach": "画像またはファイルを添付",
    "needText": "件名または説明を入力してください",
    "vendorHint": "QubePro 製品、ライセンス、サブスクリプション、プラットフォームの問題",
    "internalHint": "自社のユーザーと社内業務",
    "categories": {
      "REQUEST": "依頼",
      "ISSUE": "問題",
      "SUGGESTION": "提案",
      "QUESTION": "質問",
      "OTHER": "その他"
    },
    "priorities": {
      "low": "低",
      "medium": "中",
      "high": "高",
      "urgent": "緊急"
    }
  }
} as const;
export function supportFormCopy(language = 'tr') {
  const key = language.toLowerCase().split('-')[0] as keyof typeof FORM_COPY;
  return FORM_COPY[key] ?? FORM_COPY.en;
}
